import { IWheelOfFortunePrize, IWheelOfFortuneReward } from 'models/wheel-of-fortune/i-wheel-of-fortune';

import rmv from 'assets/wheel-of-fortune/rmv.png';
import vipPoint from 'assets/vip_point.png';

import { RewardKind, WheelData } from './WheelOfFortune/types';

/**
 * Turns an API prize into what the wheel paints.
 *
 * The wheel shows a number and an icon per segment — the number comes from the prize's
 * amount and the icon says what that number is *of*. That is why `kind` has to survive
 * the mapping: "2" means two spins, two lootboxes or 2 RMV depending on it, and the
 * segment has no room to spell it out.
 */

/** Maps the API's prize kind onto what the wheel UI distinguishes. */
export const rewardKindOf = (prize: IWheelOfFortunePrize): RewardKind => {
  switch (prize.kind) {
    // RMV is the only token a wheel pays, so `token` collapses to the RMV treatment.
    case 'token':
      return 'rmv';
    case 'vip_points':
      return 'vip_points';
    case 'wof_spins':
      return 'wof_spins';
    case 'vip_wof_spins':
      return 'vip_wof_spins';
    case 'lootbox':
      return 'lootbox';
    default:
      return 'nft';
  }
};

/**
 * The amount a prize pays, as a plain string.
 *
 * `token` carries whole RMV in `token_amount` (the API converts out of wei); everything
 * else is a count in `per_person_count`. Falling back to "1" rather than "" matters —
 * the modal gates its loader on the first segment's text being non-empty, so an
 * unrecognised prize would otherwise hang the wheel on a spinner forever.
 */
export const prizeAmount = (prize: IWheelOfFortunePrize): string => {
  if (prize.kind === 'token') {
    return prize.token_amount ?? '0';
  }
  return String(prize.per_person_count ?? 1);
};

const FALLBACK_ICON: Record<RewardKind, string> = {
  rmv,
  vip_points: vipPoint,
  wof_spins: vipPoint,
  vip_wof_spins: vipPoint,
  lootbox: vipPoint,
  nft: vipPoint,
};

/** Prefer the API's icon — it is what the admin configured on the prize. */
export const prizeIcon = (prize: IWheelOfFortunePrize, kind: RewardKind): string =>
  prize.icon_url || FALLBACK_ICON[kind];

export const rewardToWheelData = (reward: IWheelOfFortuneReward): WheelData => {
  const kind = rewardKindOf(reward.prize);
  return {
    option: prizeAmount(reward.prize),
    rewardId: reward.id,
    rewardKind: kind,
    iconUrl: prizeIcon(reward.prize, kind),
    prizeName: reward.prize.name,
  };
};
